/**
 * VizFormatModal - Plotly chart formatting options (titles, legend, colors).
 *
 * Props:
 *   open              {boolean}
 *   onClose           {fn}
 *   formatting        {object}  - current formatting config
 *   onChange          {fn}      - called with updated formatting object
 *   hasDualAxis       {boolean} - show Y2 title field
 *   hasSeries         {boolean} - show legend-related fields
 */

import { COLOR_PALETTES } from '../../lib/vizPivotGenerator';

const PALETTE_NAMES = [
  { key: 'default',    label: 'Default' },
  { key: 'pastel',     label: 'Pastel' },
  { key: 'bold',       label: 'Bold' },
  { key: 'earth',      label: 'Earth' },
  { key: 'monochrome', label: 'Mono' },
];

const LEGEND_POSITIONS = [
  { value: 'top-right',    label: 'Top Right' },
  { value: 'top-left',     label: 'Top Left' },
  { value: 'bottom-right', label: 'Bottom Right' },
  { value: 'bottom-left',  label: 'Bottom Left' },
];

function Field({ label, hint, children }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-36 shrink-0">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        {hint && <p className="text-xs text-gray-400">{hint}</p>}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function TextInput({ value, onChange, placeholder }) {
  return (
    <input
      type="text"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none"
    />
  );
}

function Select({ value, onChange, options }) {
  return (
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      className="px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none"
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

export default function VizFormatModal({ open, onClose, formatting = {}, onChange, hasDualAxis, hasSeries }) {
  if (!open) return null;

  const set = (key, val) => onChange({ ...formatting, [key]: val });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-base font-semibold text-gray-900">Chart Formatting</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        <div className="px-6 py-4 space-y-6">

          {/* Section 1: Titles */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Titles</h3>
            <div className="space-y-3">
              <Field label="Chart Title" hint="Blank = auto">
                <TextInput
                  value={formatting.chartTitle}
                  onChange={v => set('chartTitle', v)}
                  placeholder="e.g. Revenue by Quarter"
                />
              </Field>
              <Field label="X Axis Title" hint="Blank = field name">
                <TextInput
                  value={formatting.xTitle}
                  onChange={v => set('xTitle', v)}
                  placeholder="e.g. Quarter"
                />
              </Field>
              <Field label="Y Axis Title" hint="Blank = auto">
                <TextInput
                  value={formatting.yTitle}
                  onChange={v => set('yTitle', v)}
                  placeholder="e.g. Revenue ($)"
                />
              </Field>
              {hasDualAxis && (
                <Field label="Y2 Axis Title" hint="Right axis">
                  <TextInput
                    value={formatting.y2Title}
                    onChange={v => set('y2Title', v)}
                    placeholder="e.g. Count"
                  />
                </Field>
              )}
              {hasSeries && (
                <Field label="Legend Title" hint="Blank = field name">
                  <TextInput
                    value={formatting.legendTitle}
                    onChange={v => set('legendTitle', v)}
                    placeholder="e.g. Industry"
                  />
                </Field>
              )}
            </div>
          </section>

          {/* Section 2: Legend */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Legend</h3>
            <div className="space-y-3">
              <Field label="Show Legend">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formatting.showLegend !== false}
                    onChange={e => set('showLegend', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-600">Visible</span>
                </label>
              </Field>
              <Field label="Position">
                <Select
                  value={formatting.legendPos || 'top-right'}
                  onChange={v => set('legendPos', v)}
                  options={LEGEND_POSITIONS}
                />
              </Field>
              <Field label="Orientation">
                <Select
                  value={formatting.legendOrientation || 'v'}
                  onChange={v => set('legendOrientation', v)}
                  options={[
                    { value: 'v', label: 'Vertical' },
                    { value: 'h', label: 'Horizontal' },
                  ]}
                />
              </Field>
            </div>
          </section>

          {/* Section 3: Colors & Font */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Colors &amp; Font</h3>
            <div className="space-y-3">
              <Field label="Color Palette">
                <div className="flex gap-2 flex-wrap">
                  {PALETTE_NAMES.map(({ key, label }) => {
                    const colors = COLOR_PALETTES[key];
                    const isSelected = (formatting.colorPalette || 'default') === key;
                    return (
                      <button
                        key={key}
                        onClick={() => set('colorPalette', key)}
                        title={label}
                        className={`flex flex-col items-center gap-1 p-1.5 rounded border-2 transition ${
                          isSelected ? 'border-primary-500' : 'border-transparent hover:border-gray-300'
                        }`}
                      >
                        <div className="flex gap-0.5">
                          {colors.slice(0, 4).map((c, i) => (
                            <div key={i} className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: c }} />
                          ))}
                        </div>
                        <span className="text-xs text-gray-500">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field label="Font Size">
                <Select
                  value={formatting.fontSize || '12'}
                  onChange={v => set('fontSize', v)}
                  options={[
                    { value: '10', label: '10px' },
                    { value: '12', label: '12px (default)' },
                    { value: '14', label: '14px' },
                    { value: '16', label: '16px' },
                  ]}
                />
              </Field>
              <Field label="Background">
                <Select
                  value={formatting.bgColor || 'white'}
                  onChange={v => set('bgColor', v)}
                  options={[
                    { value: 'white',       label: 'White' },
                    { value: 'light-gray',  label: 'Light Gray' },
                    { value: 'transparent', label: 'Transparent' },
                  ]}
                />
              </Field>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
